import React, { useEffect, useId, useRef, useState } from 'react';
import { message } from 'antd';
import { PhotoIcon, VideoCameraIcon, VideoCameraSlashIcon } from '@heroicons/react/24/outline';
import { TWButton } from '../../../components/ui';
import { validateUploadFile } from '../../../utils/fileValidation';
import { detectVideoCodec, readFileBytes, videoCodecVerdict } from '../video-codec';
import { ACCEPT_IMAGE, ACCEPT_VIDEO, MAX_IMAGE, MAX_VIDEO, formatBytes } from '../release-note-editor-model';

const copy = {
  image: { accept: ACCEPT_IMAGE, max: MAX_IMAGE, pick: 'Choose an image', hint: 'jpeg, png, webp, gif · up to 6 MB', limit: 'up to 6 MB', type: 'Use jpeg, png, webp or gif', size: 'Images can be at most 6 MB', Icon: PhotoIcon },
  video: { accept: ACCEPT_VIDEO, max: MAX_VIDEO, pick: 'Choose a video', hint: 'mp4, webm · up to 80 MB', limit: 'up to 80 MB', type: 'Use mp4 or webm', size: 'Videos can be at most 80 MB', codecNote: 'Use MP4 with H.264 or WebM with VP8/VP9: they play in every browser. iPhone and Mac screen recordings are HEVC: export them as H.264 first.', Icon: VideoCameraIcon },
};

const extensionOf = (name, mimetype) => {
  const parts = String(name || '').split('.');
  const ext = parts.length > 1 ? parts.pop() : String(mimetype || '').split('/').pop();
  return (ext || 'file').toUpperCase();
};

// An image or video block's file (spec §7.3). The real <input type="file"> is
// in the DOM (sr-only, focusable through the drop zone's peer ring) so QA can
// setInputFiles and keyboards reach it. `onPick` receives a validated File;
// the page uploads it. "Replace" is another upload — the editor never deletes
// server media (spec §7.4).
// `canUpload` is false while the note is unsaved OR busy; the "save first"
// hint follows `saved` only, so a busy saved note does not show it.
const MediaField = ({ block, uploading, canUpload, saved = true, onPick }) => {
  const c = copy[block.type];
  const inputId = useId();
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  useEffect(() => {
    setPreviewFailed(false);
  }, [block.mediaurl]);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const result = validateUploadFile(file, { accept: c.accept, maxBytes: c.max });
    if (!result.ok) {
      const text = result.reason === 'size' ? c.size : c.type;
      setLocalError(text);
      message.error(text);
      return;
    }
    if (block.type === 'video') {
      const verdict = videoCodecVerdict(await detectVideoCodec(readFileBytes(file), file.size));
      if (!verdict.ok) {
        setLocalError(verdict.message);
        message.error(verdict.message);
        return;
      }
    }
    setLocalError('');
    onPick(file);
  };

  const fileLabel = block.filename || String(block.mediaurl || '').split('/').pop();
  const meta = [extensionOf(block.filename, block.mediamimetype), block.sizebytes ? formatBytes(block.sizebytes) : null, c.limit]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-col gap-2">
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={c.accept}
        className="peer sr-only"
        tabIndex={block.mediaurl ? -1 : 0}
        onChange={onChange}
        disabled={!canUpload || uploading}
      />
      {block.mediaurl ? (
        <div className="flex flex-wrap items-center gap-3">
          {block.type === 'image' ? (
            <img src={block.mediaurl} alt="" className="aspect-video w-32 rounded-md border border-slate-200 object-cover dark:border-slate-700" />
          ) : previewFailed ? (
            <div className="flex aspect-video w-32 flex-col items-center justify-center gap-1 rounded-md border border-slate-200 bg-slate-50 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              <VideoCameraSlashIcon className="h-5 w-5" aria-hidden="true" />
              Can't preview
            </div>
          ) : (
            <video src={block.mediaurl} preload="metadata" muted onError={() => setPreviewFailed(true)} className="aspect-video w-32 rounded-md border border-slate-200 bg-slate-950 object-contain dark:border-slate-700" />
          )}
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{fileLabel}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">{meta}</span>
            <TWButton
              size="sm"
              label={uploading ? 'Uploading…' : 'Replace'}
              loading={uploading}
              disabled={!canUpload}
              onClick={() => inputRef.current?.click()}
              className="self-start"
            />
          </div>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-6 text-center hover:border-slate-400 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 dark:border-slate-600 dark:peer-focus-visible:ring-sky-400"
        >
          <c.Icon className="h-6 w-6 text-slate-400 dark:text-slate-500" aria-hidden />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{uploading ? 'Uploading…' : `${c.pick} (${c.hint})`}</span>
          {!saved ? <span className="text-xs text-slate-500 dark:text-slate-400">Save the draft first to add images or videos.</span> : null}
        </label>
      )}
      {c.codecNote ? <p className="text-xs text-slate-500 dark:text-slate-400">{c.codecNote}</p> : null}
      {localError ? <p className="text-xs text-rose-600 dark:text-rose-400">{localError}</p> : null}
    </div>
  );
};

export default MediaField;
